// Shared rules for copying a pipeline (seller/buyer interest) record onto its Leads contact.
// Used by the app (sellerInterestService, buyerInterestService) and, through a generated CommonJS copy
// (functions/mcp/pipelineContact.js), by the AI connector. Pure functions only: no database calls here.

export const CONTACT_TYPES = ['Buyer', 'Seller', 'Tenant', 'Landlord', 'Agent', 'Vendor', 'Other'];

const str = (v) => (v == null ? '' : String(v).trim());
const onlyDigits = (v) => str(v).replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

export const pipelineContactType = (kind) => (kind === 'buyer' ? 'Buyer' : 'Seller');

// Everything from the pipeline record that belongs on the contact.
export const contactFieldsFromInterest = (kind, it) => {
  const name = [str(it.firstName), str(it.lastName)].filter(Boolean).join(' ') || str(it.name);
  const f = {
    firstName: str(it.firstName), lastName: str(it.lastName), name, email: str(it.email), phone: str(it.phone),
    consentText: !!it.consentText, consentEmail: !!it.consentEmail,
    source: [str(it.howHeard === 'Other' ? it.howHeardOther : it.howHeard), str(it.leadSource)].find((x) => x && !/^(seller|buyer) interest$/i.test(x)) || '',
    notes: str(it.notes),
    additionalContacts: (Array.isArray(it.additionalContacts) ? it.additionalContacts : []).filter((c) => c && (str(c.firstName) || str(c.lastName) || str(c.phone) || str(c.email)))
      .map((c) => ({ firstName: str(c.firstName), lastName: str(c.lastName), phone: str(c.phone), email: str(c.email), relationship: str(c.relationship) || 'Spouse' })),
    contactTypes: [pipelineContactType(kind)],
  };
  if (kind === 'seller') {
    f.address = str(it.propertyAddress); f.propertyAddress = str(it.propertyAddress); f.community = str(it.community);
    f.alsoBuying = str(it.alsoBuying); f.reasonForMoving = str(it.reasonForMoving);
    if (/^yes$/i.test(f.alsoBuying)) f.contactTypes.push('Buyer');
  } else {
    f.interestedCity = str(it.areas); f.priceMin = str(it.priceMin); f.priceMax = str(it.priceMax);
    f.alsoSelling = str(it.alsoSelling); f.reasonForMoving = str(it.reasonForMoving);
    if (/^yes$/i.test(f.alsoSelling)) f.contactTypes.push('Seller');
  }
  return f;
};

// New contact: everything. Existing contact: only filled-in values (never blanks anything), types are
// added to (never removed), consents only turn on, and pipeline notes are appended once.
export const contactPatch = (existing, fields, isNew) => {
  if (isNew) return { ...fields };
  const ex = existing || {};
  const patch = {};
  for (const [k, v] of Object.entries(fields)) {
    if (k === 'contactTypes') { patch.contactTypes = [...new Set([...(Array.isArray(ex.contactTypes) ? ex.contactTypes : []), ...v])]; continue; }
    if (k === 'consentText' || k === 'consentEmail') { if (v) patch[k] = true; continue; }
    if (k === 'notes') { if (v && !str(ex.notes).includes(v)) patch.notes = str(ex.notes) ? `${str(ex.notes)}\n\n${v}` : v; continue; }
    // The real source the user picked (e.g. Community Farming) replaces an empty or generic "Seller/Buyer interest" source.
    if (k === 'source') { if (v && (!str(ex.source) || /^(seller|buyer) interest$/i.test(str(ex.source)))) patch.source = v; continue; }
    if (k === 'additionalContacts') {
      if (!v.length) continue;
      const have = Array.isArray(ex.additionalContacts) ? ex.additionalContacts : [];
      const key = (c) => `${onlyDigits(c.phone)}|${str(c.email).toLowerCase()}|${str(c.firstName).toLowerCase()}`;
      const byKey = new Map(have.map((c) => [key(c), c]));
      v.forEach((c) => byKey.set(key(c), { ...(byKey.get(key(c)) || {}), ...c }));
      patch.additionalContacts = [...byKey.values()];
      continue;
    }
    if (v !== '' && v != null) patch[k] = v;
  }
  if (existing && existing.dnc && patch.phone) patch.dncFlag = 'A phone number was re-added to a Do Not Call contact. Calls and texts stay blocked.';
  return patch;
};

// Finds a contact by email or phone. contacts: { id: contact }.
export const findContactId = (contacts, { email, phone }) => {
  const e = str(email).toLowerCase(); const p = onlyDigits(phone);
  const hit = Object.entries(contacts || {}).find(([, c]) => c && !c.mergedInto && ((e && str(c.email).toLowerCase() === e) || (p && p.length >= 10 && (onlyDigits(c.phone) === p || ((c.optOut && c.optOut.dnc && c.optOut.dnc.removedPhones) || []).includes(p)))));
  return hit ? hit[0] : null;
};

// Referral follow-up task titles (tasks only; nothing is sent to the referrer).
export const referralNotifyTitle = (referrer, name) => `Let ${referrer} know you're working with ${name}`;
export const referralGiftTitle = (referrer) => `Send ${referrer} a thank-you gift`;
